// Cspell:ignore plts appt
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { shipmentTagsColumn } from "../../../utils/tableColumns/commons";

import {
  sortByPlannedStatus,
  sortDatesAsc,
  sortNumbersAsc,
  sortRowByPresetRoute,
  sortServiceDue,
  sortShipmentsByAssignment,
} from "../../../utils/sortComparator";
import {
  formatEtaTableField,
  formatServiceDueTableField,
} from "../../../utils/tableFormat";
import { dashIfEmpty, formatNumber } from "../../../constants/utils";
import AssignmentText from "../../../pages/inbound/prePlanning/AssignmentText";
import { TableShipment } from "../old-table/ShipmentsTableBase";
import PlannedShipmentStatusTag from "../StatusTag/PlannedShipmentStatusTag";
import { AppointmentDateTimeField } from "../../../pages/inbound/shared/AppointmentDateTimeField";
import { InboundTrailerIcon } from "../../../pages/inbound/shared/InboundTrailerIcon";
import { CareOfDeliveryCell } from "../../../pages/inbound/shared/CareOfDeliveryCell";
import { isMexicoLTLService } from "../../../pages/inbound/shared/utils";
import { SHIPMENT_TABLE_COLUMN_NAME } from "../../../constants/TableColumnNames";
import { handlingUnitsTransformer } from "../../../utils/handlingUnitsDashTransformer";
import TableLink from "./TableLink";

type ShipmentColumnsArgs = {
  timeZone: string;
  showPresetRouteColumn: boolean;
  selectedDate: string;
  isMexicoTooltipEnabled?: boolean;
  isHandlingUnitsEnabled?: boolean;
};

export const shipmentColumns = ({
  timeZone,
  showPresetRouteColumn,
  selectedDate,
  isMexicoTooltipEnabled = false,
  isHandlingUnitsEnabled = false,
}: ShipmentColumnsArgs): ColumnDef<TableShipment, any>[] => [
  {
    accessorKey: "proNumber",
    header: "PRO",
  },
  ...(showPresetRouteColumn
    ? [
        {
          accessorKey: "presetRouteName",
          header: "Pre-Set Route",
          accessorFn: ({ presetRouteName }: { [key: string]: any }) =>
            dashIfEmpty(presetRouteName),
          sortingFn: sortRowByPresetRoute,
        },
      ]
    : []),
  {
    accessorKey: "destCity",
    header: "Dest City, Zip",
    accessorFn: ({ consignee, serviceLevel, careOfDelivery }) =>
      isMexicoLTLService(isMexicoTooltipEnabled, serviceLevel)
        ? `${dashIfEmpty(careOfDelivery?.city)}, ${dashIfEmpty(careOfDelivery?.zipCode)}`
        : `${dashIfEmpty(consignee?.city)}, ${dashIfEmpty(consignee?.zipCode)}`,
  },
  {
    accessorKey: "serviceDue",
    header: "Service Due",
    accessorFn: ({ serviceDue }) => formatServiceDueTableField(serviceDue),
    sortingFn: sortServiceDue,
  },
  {
    accessorKey: "appointmentDate",
    header: "Appointment",
    cell: ({ row }: CellContext<TableShipment, unknown>) => (
      <AppointmentDateTimeField
        startDate={row.original.appointmentDate}
        endDate={row.original.appointmentEndDate}
        timeZone={timeZone}
      />
    ),
    sortingFn: (rowA, rowB) =>
      sortDatesAsc(
        rowA.original.appointmentDate,
        rowB.original.appointmentDate
      ),
  },
  {
    accessorKey: "consignee",
    header: "Consignee",
    accessorFn: ({ consignee, careOfDelivery, serviceLevel }) =>
      isMexicoLTLService(isMexicoTooltipEnabled, serviceLevel)
        ? dashIfEmpty(careOfDelivery?.name)
        : dashIfEmpty(consignee?.name),
    cell: ({ row }) => (
      <CareOfDeliveryCell
        {...row.original}
        isMexicoTooltipEnabled={isMexicoTooltipEnabled}
      />
    ),
  },
  {
    accessorKey: "shipper",
    header: "Shipper",
    accessorFn: ({ shipper }) => dashIfEmpty(shipper?.name),
  },
  {
    accessorKey: "pltsPcs",
    header: "Plts | Pcs",
    accessorFn: ({ pallets, pieces }) =>
      `${formatNumber(pallets)} | ${formatNumber(pieces)}`,
    cell: ({ getValue }) => (
      <div style={{ minWidth: "90px" }}>{getValue()}</div>
    ),
    sortingFn: (rowA, rowB) =>
      sortNumbersAsc(rowA.original.pallets, rowB.original.pallets),
    sortDescFirst: false,
  },
  ...((isHandlingUnitsEnabled
    ? [
        {
          accessorKey: "handlingUnits",
          header: SHIPMENT_TABLE_COLUMN_NAME.HANDLING_UNITS,
          accessorFn: (row) => handlingUnitsTransformer(row.handlingUnits),
          sortingFn: (rowA, rowB) =>
            sortNumbersAsc(
              rowA.original.handlingUnits || 0,
              rowB.original.handlingUnits || 0
            ),
        },
      ]
    : []) as ColumnDef<TableShipment>[]),
  {
    accessorKey: "weight",
    header: "Weight",
    accessorFn: ({ weight, weightUnit }) =>
      `${formatNumber(weight)} ${weightUnit}`,
    sortingFn: (rowA, rowB) =>
      sortNumbersAsc(rowA.original.weight, rowB.original.weight),
  },
  shipmentTagsColumn("tags"),
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ getValue, row }: CellContext<TableShipment, string>) =>
      getValue() ? (
        <PlannedShipmentStatusTag
          label={getValue<string>()}
          isPlannedForToday={row.original.isPlanned}
          futurePlanDates={row.original.futurePlanDates}
          selectedDate={selectedDate}
        />
      ) : null,
    sortingFn: (rowA, rowB) =>
      sortByPlannedStatus(rowA.original, rowB.original),
  },
  {
    accessorKey: "location",
    header: "Trailer #",
    accessorFn: ({ location }) => dashIfEmpty(location),
  },
  {
    accessorKey: "sic",
    header: "Dest SIC",
    sortDescFirst: false,
  },
  {
    accessorKey: "etaDate",
    header: "ETA",
    accessorFn: ({ etaDate }) => formatEtaTableField(etaDate, timeZone),
    sortingFn: (rowA, rowB) =>
      sortDatesAsc(rowA.original.etaDate, rowB.original.etaDate),
  },
  {
    accessorKey: "assignment",
    header: "Assignment",
    sortingFn: (rowA, rowB) =>
      sortShipmentsByAssignment(
        rowA.original.assignment ?? undefined,
        rowB.original.assignment ?? undefined
      ),
    cell: ({ row }: CellContext<TableShipment, string>) => {
      const { id, assignment, assignmentId, type, isCommitted } = row.original;

      if (!assignment || assignment === "Unassigned") {
        return <AssignmentText text={assignment} />;
      }
      return (
        <TableLink
          id={String(assignmentId)}
          title={assignment}
          iconComponent={
            <InboundTrailerIcon
              isLockedRoute={Boolean(isCommitted)}
              icon={type === "delivery_route" ? "route" : "trap"}
              size="small"
            />
          }
          planType={type ?? "delivery_route"}
          inboundPage="planning"
          tab="list"
          selection={[String(id)]}
        >
          {assignment}
        </TableLink>
      );
    },
  },
];
